import { gql } from '@apollo/client'

// USER & ACCOUNT MANAGEMENT

const customRevisionEntity = gql`
  fragment CustomRevisionEntity on CustomRevisionEntity {
    id
    projectId
    revisionDate
    modifiedBy
    entityRevTypes {
      entityName
      entityId
      revisionType
    }
  }
`

const page = gql`
  fragment Page on Page {
    totalPages
    totalElements
    number
    size
  }
`

const company = gql`
  fragment Company on Company {
    id
    name
    sector
    location
    companyURL
    companyLogoURL
    exist
    invest
    districtCourt
    registrationNumber
    information
    address {
      additional
      street
      city
      postcode
      country
    }
  }
`

const userGroup = gql`
  fragment UserGroup on UserGroup {
    id
    name
    organizationType
    owner {
      userId
      email
      firstname
      lastname
    }
    members {
      userId
      email
      firstname
      lastname
      avatarURL
    }
    packages {
      orderId
      creationDate
      contingents
      validUntil
      expired
      appliedContingents {
        id
        projectId
        usedBy: userId
        timestamp
      }
    }
  }
`

const userAccount = gql`
  fragment UserAccount on UserAccount {
    userId
    expertType
    userStatus
    group {
      ...UserGroup
    }
    email
    firstname
    lastname
    title
    avatarURL
    newsSubscription
    agreedLatestAGB
    signupTime
    lastLogin
    lastProfileUpdate
    numberOfLogins
    business {
      id
      name
      companyLogoURL
      address {
        street
        postcode
        city
        country
        additional
      }
    }
  }
  ${userGroup}
`

const userGroupInvitation = gql`
  fragment UserGroupInvitation on UserGroupInvitation {
    userEmail
    status
  }
`

const projectMembershipBase = gql`
  fragment ProjectMembershipBase on ProjectMembership {
    userAccount {
      userId
      firstname
      lastname
      email
      business {
        name
      }
      group {
        id
      }
    }
    projectRole
    defaultInvestor {
      id
    }
  }
`
const projectMembership = gql`
  fragment ProjectMembership on ProjectMembership {
    ...ProjectMembershipBase
    defaultInvestor {
      id
      name
      type
    }
  }
  ${projectMembershipBase}
`

const projectInvitationBase = gql`
  fragment ProjectInvitationBase on ProjectInvitation {
    userEmail
    guestName
    projectRole
    status
    defaultInvestor {
      id
    }
  }
`
const projectInvitation = gql`
  fragment ProjectInvitation on ProjectInvitation {
    ...ProjectInvitationBase
    defaultInvestor {
      id
      name
      type
    }
  }
  ${projectInvitationBase}
`

const projectDocumentBase = gql`
  fragment ProjectDocumentBase on ProjectDocument {
    id {
      projectId
      blobName
    }
    title
    dateAdded
    lastModified
    uploader {
      userId
    }
  }
`
const projectDocument = gql`
  fragment ProjectDocument on ProjectDocument {
    ...ProjectDocumentBase
    uploader {
      userId
      firstname
      lastname
      email
    }
  }
  ${projectDocumentBase}
`

// BASE ENTITIES: INVESTOR, ROUND, CN, PAYOUT & SHARE

const investor = gql`
  fragment Investor on Holder {
    id
    type
    name
    email
    jobTitle
    salary

    ... on TrackedEntity {
      createdDate
      modifiedDate
    }

    ... on Esop {
      dilutionType
      notes
      share {
        id
      }
    }
  }
`

const esopSetup = gql`
  fragment EsopSetup on EsopSetup {
    id
    defaultCompensationPeriodInMonths
    exitAmount
    exitDate
    bonusCompensationLevels {
      name
      percentFromSalary
    }
  }
`

const employeeLinkSetup = gql`
  fragment EmployeeLinkSetup on EmployeeLinkSetup {
    id
    text1
    text2
    header1
    header2
    defaultExitAmount
    defaultExitDate
  }
`

const employeeLink = gql`
  fragment EmployeeLink on EmployeeLink {
    id
    projectId
    employeeId
    expirationDate
    overridden
    revoked
    accessed
  }
`

const antiDilution = gql`
  fragment AntiDilution on AntiDilution {
    id
    type
    compensation
    duration
    transferType
  }
`

const share = gql`
  fragment Share on Share {
    id
    name
    commonShare
    commonShareOptions
    seniority
    votingRights
    text
    defaultPriceCalculationMethod
    liquidationPreference {
      id
      multiple
      simple
      agreedConversion
      senior
      capType
      cap
      customSharePrice
      interestRate {
        id
        interestRate
        cumulative
        accumulationType
        retainDividends
        limitInMonths
        limitAsDate
      }
    }
    antiDilution {
      ...AntiDilution
    }
  }
  ${antiDilution}
`

const shareInRound = gql`
  fragment ShareInRound on Share {
    id
    name
    commonShare
    commonShareOptions
    antiDilution {
      id
    }
    liquidationPreference {
      id
    }
    project {
      id
    }
  }
`

const roundBase = gql`
  fragment RoundBase on Round {
    id
    date
    ... on SimulationRound {
      name
      preMoneyValuation
      valuationDifference
      share {
        id
      }
    }
    ... on InvestmentRound {
      name
      preMoneyValuation
      postMoneyValuation
      cashIn
      type
      project {
        id
      }
      share {
        id
      }
    }
  }
`
const round = gql`
  fragment Round on Round {
    ...RoundBase
    ... on SimulationRound {
      share {
        ...ShareInRound
      }
    }
    ... on InvestmentRound {
      share {
        ...ShareInRound
      }
    }
  }
  ${shareInRound}
  ${roundBase}
`

const revisionRound = gql`
  fragment RoundRevision on Round {
    id
    date
    ... on SimulationRound {
      name
      preMoneyValuation
      valuationDifference
      share {
        id
        name
      }
    }
    ... on InvestmentRound {
      name
      preMoneyValuation
      share {
        id
        name
      }
    }
  }
`

const convertibleNote = gql`
  fragment ConvertibleNote on ConvertibleNote {
    id
    discount
    dateOfDeposit
    maturityDate
    preMoneyCap
    preMoneyFloor
    defaultPriceCalculationMethod
    interestRate {
      id
      interestRate
      accumulationType
      cumulative
      retainDividends
      limitInMonths
      limitAsDate
    }
    share {
      id
      name
    }
    forcedPaymentOnConversion
    createdDate
  }
`

const payOut = gql`
  fragment PayOut on PayOut {
    id
    name
    seniority
    amount
    receivers {
      id
      name
    }
    distributionTable {
      investor {
        id
        name
      }
      amount
    }
  }
`

// EVENTS (With all subtypes: Investment, EsopGrant, EsopCreation ...)

const vestingSchedule = gql`
  fragment VestingSchedule on VestingSchedule {
    startDate
    percentUnderlyingVesting
    cliff
    cliffPercent
    accelerationOnExit
    schedule {
      percent
      months
      vestingTime
    }
  }
`

const esopBase = gql`
  fragment EsopBase on EsopCreation {
    id
    type
    round {
      id
    }
    esop {
      id
    }
    valuationType
    shareAmount
    backingTable {
      investor {
        id
      }
      numberOfShares
    }
    defaultVestingSchedule {
      ...VestingSchedule
    }
  }
  ${vestingSchedule}
`
const esop = gql`
  fragment EsopFragment on EsopCreation {
    ...EsopBase
    round {
      id
      date
    }
    esop {
      id
      name
      dilutionType
      notes
      share {
        id
      }
      modifiedDate
    }
    backingTable {
      investor {
        id
        name
      }
      numberOfShares
    }
  }
  ${esopBase}
`
const esopGrantBase = gql`
  fragment EsopGrantBase on EsopGrant {
    id
    receiver {
      id
    }
    round {
      id
    }
    strikePrice
    date
    expirationDate
    shareAmount
    esop {
      id
    }
    vestingSchedule {
      ...VestingSchedule
    }
    packageCalculation {
      packageValue
      salaryCompensation
      bonusCompensation
      compensationPeriodInMonths
      percentOfPackageBackedByThisGrant
    }
  }
  ${vestingSchedule}
`
const esopGrant = gql`
  fragment EsopGrantFragment on EsopGrant {
    ...EsopGrantBase
    receiver {
      id
      name
    }
    round {
      id
      ... on InvestmentRound {
        name
      }
    }
    esop {
      id
      name
    }
  }
  ${esopGrantBase}
`
const investmentBase = gql`
  fragment InvestmentBase on Event {
    id
    type
    date
    round {
      id
    }
    ... on Investment {
      investor {
        id
      }
      amount
      ... on EquityInvestment {
        pricePerShare
        amountOfShares
        share {
          id
        }
        vestingSchedule {
          ...VestingSchedule
        }
      }
      ... on CnInvestment {
        convertibleNote {
          ...ConvertibleNote
        }
      }
      ... on CnConversion {
        pricePerShare
        amountOfShares
        share {
          id
        }
        interestAmount
        nominalAmount
        convertibleNote {
          id
        }
      }
    }
  }
  ${vestingSchedule}
  ${convertibleNote}
`
const investment = gql`
  fragment Investment on Event {
    ...InvestmentBase
    round {
      id
      ... on InvestmentRound {
        name
      }
    }
    ... on Investment {
      investor {
        id
        name
      }
      ... on EquityInvestment {
        share {
          id
          name
        }
      }
      ... on CnConversion {
        share {
          id
          name
        }
        convertibleNote {
          ...ConvertibleNote
        }
      }
    }
  }
  ${investmentBase}
  ${convertibleNote}
`

const buyOutBase = gql`
  fragment BuyOutBase on Event {
    id
    type
    date
    round {
      id
    }
    ... on BuyOut {
      buyer {
        id
      }
      seller {
        id
      }
      amount
      pricePerShare
      buyingShare {
        id
      }
      sellingShare {
        id
      }
      vestingSchedule {
        ...VestingSchedule
      }
    }
  }
  ${vestingSchedule}
`
const buyOut = gql`
  fragment BuyOut on Event {
    ...BuyOutBase
    round {
      id
      date
      ... on InvestmentRound {
        name
        preMoneyValuation
      }
    }
    ... on BuyOut {
      buyer {
        id
        name
      }
      seller {
        id
        name
      }
      buyingShare {
        id
        name
      }
      sellingShare {
        id
        name
      }
    }
  }
  ${buyOutBase}
`

const manualSharesBase = gql`
  fragment ManualSharesBase on Event {
    id
    type
    date
    round {
      id
    }
    ... on ManualShares {
      investor {
        id
      }
      share {
        id
      }
      name
      investment
      price
      amountOfShares
      isVirtualBacking
      notes
    }
  }
`
const manualShares = gql`
  fragment ManualShares on Event {
    ...ManualSharesBase
    round {
      id
      date
      ... on InvestmentRound {
        name
        preMoneyValuation
      }
    }
    ... on ManualShares {
      investor {
        id
        name
      }
      share {
        id
        name
        commonShare
        commonShareOptions
      }
    }
  }
  ${manualSharesBase}
`

const manualSharesRevision = gql`
  fragment ManualSharesRevision on Event {
    id
    type
    round {
      id
      ... on InvestmentRound {
        name
      }
    }
    ... on ManualShares {
      investor {
        id
        name
      }
      share {
        id
        name
      }
      name
      investment
      price
      amountOfShares
      isVirtualBacking
      notes
    }
  }
`

const adEventBase = gql`
  fragment AdEventBase on Event {
    id
    type
    round {
      id
    }
    ... on ADEvent {
      antiDilution {
        id
        type
        transferType
        compensation
        duration
      }
      capitalIncrease
      adPrice
      roundPrice
    }
  }
`

const dividendsEventBase = gql`
  fragment DividendsEventBase on Event {
    id
    type
    round {
      id
    }
    ... on DividendsEvent {
      interestReceipt {
        interestRate {
          id
          interestRate
          cumulative
          accumulationType
          retainDividends
          limitInMonths
          limitAsDate
        }
        startDate
        endDate
        appliedEndDate
        time
        timeFloored
        timeRemaining
        denominator
        principalAmount
        interestAmount
      }
    }
  }
`
const dividendsEvent = gql`
  fragment DividendsEvent on Event {
    ...DividendsEventBase
    round {
      id
      date
      ... on InvestmentRound {
        name
        preMoneyValuation
      }
    }
  }
  ${dividendsEventBase}
`

const eventBase = gql`
  fragment EventBase on Event {
    id
    type
    ... on Investment {
      ...InvestmentBase
    }
    ... on EsopCreation {
      ...EsopBase
    }
    ... on EsopGrant {
      ...EsopGrantBase
    }
    ... on BuyOut {
      ...BuyOutBase
    }
    ... on ManualShares {
      ...ManualSharesBase
    }
    round {
      id
    }
  }
  ${buyOutBase}
  ${investmentBase}
  ${esopBase}
  ${esopGrantBase}
  ${manualSharesBase}
`
const event = gql`
  fragment Event on Event {
    ...EventBase
    round {
      id
      date
      ... on InvestmentRound {
        name
        preMoneyValuation
      }
    }
    ... on EsopCreation {
      ...EsopFragment
    }
    ... on EsopGrant {
      ...EsopGrantFragment
    }
    ... on BuyOut {
      ...BuyOut
    }
    ... on ManualShares {
      ...ManualShares
    }
    ... on DividendsEvent {
      ...DividendsEvent
    }
    ... on Investment {
      ...Investment
    }
  }
  ${eventBase}
  ${buyOut}
  ${investment}
  ${esop}
  ${esopGrant}
  ${manualShares}
  ${dividendsEvent}
`

// ENTRIES and PARTICIPATIONS
// Important Note: We only fetch SimulationEvents (DividendsEvent, ADEvent) as they are not included in project.events (never saved)
const entryBase = gql`
  fragment EntryBase on Entry {
    id
    type
    operation
    amount
    round {
      id
    }
    investor {
      id
    }
    event {
      id
      ... on ADEvent {
        ...AdEventBase
      }
      ... on DividendsEvent {
        ...DividendsEventBase
      }
    }
    ... on ShareEntry {
      share {
        id
      }
    }
    ... on LpEntry {
      originalInvestmentDate
      originalLpNominalSigned
    }
    ... on CnEntry {
      convertibleNote {
        id
      }
    }
  }
  ${dividendsEventBase}
  ${adEventBase}
`
const entry = gql`
  fragment Entry on Entry {
    ...EntryBase
    round {
      id
      date
      ... on InvestmentRound {
        name
        preMoneyValuation
      }
    }
    investor {
      id
      name
    }
    event {
      ...Event
    }
    ... on ShareEntry {
      share {
        id
        seniority
        name
      }
    }
    ... on CnEntry {
      convertibleNote {
        id
        discount
        preMoneyCap
        preMoneyFloor
      }
    }
  }
  ${event}
  ${entryBase}
`

const participationBase = gql`
  fragment ParticipationBase on Participation {
    id
    type
    amount
    investor {
      id
    }
    round {
      id
    }
    ... on ShareParticipation {
      amountLegal
      amountVested
      amountEsop
      amountExit
      share {
        id
      }
      backedAmount
      dividends
    }
    ... on CnParticipation {
      convertibleNote {
        id
      }
      nominal
      dividends
      dividendsRoundBasis
    }
    ... on EsopParticipation {
      amountLegal
      amountVested
      amountEsop
      amountExit
      share {
        id
      }
      poolSize
      poolAvailable
    }
    ... on GrantedParticipation {
      amountLegal
      amountVested
      amountEsop
      amountExit
      share {
        id
      }
    }
    entries {
      ...EntryBase
    }
  }
  ${entryBase}
`
const participationTrace = gql`
  fragment ParticipationTrace on Participation {
    ...ParticipationBase
    investor {
      ...Investor
    }
    round {
      id
      date
      ... on InvestmentRound {
        name
        preMoneyValuation
      }
    }
    ... on ShareParticipation {
      share {
        id
        seniority
        name
      }
    }
    ... on CnParticipation {
      convertibleNote {
        ...ConvertibleNote
      }
    }
    ... on EsopParticipation {
      share {
        id
        seniority
        name
      }
    }
    ... on GrantedParticipation {
      share {
        id
        seniority
        name
      }
    }
    entries {
      ...Entry
    }
  }
  ${investor}
  ${convertibleNote}
  ${participationBase}
  ${entry}
`

// EXITS (sharePoint, shareReturn, InvestorReturn..)

const returnEntry = gql`
  fragment ReturnEntry on ReturnEntry {
    exitRight {
      ... on Participation {
        id
        ... on ShareParticipation {
          share {
            id
            name
          }
        }
      }
      ... on PayOut {
        name
      }
    }
    exitRightType

    payoutLevel
    amount

    payoutQuota
  }
`

const returnStep = gql`
  fragment ReturnStep on ReturnStep {
    payoutLevel
    returnEntries {
      ...ReturnEntry
    }
  }
  ${returnEntry}
`

const sharePointBase = gql`
  fragment SharePointBase on SharePoint {
    share {
      id
    }
    totalPreferenceAmount
    totalNumberOfShares
    capPoint
    conversionPoint
  }
`
const sharePoint = gql`
  fragment SharePoint on SharePoint {
    ...SharePointBase
    share {
      id
      name
      seniority
    }
    preferenceAmounts {
      investor {
        id
        name
      }
      amount
    }
  }
  ${sharePointBase}
`

const shareReturnBase = gql`
  fragment ShareReturnBase on ShareReturn {
    id
    share {
      id
    }
    investors {
      id
      name
    }
    amount
    totalNumberOfShares
    exitPrice
    exitPriceLP
    exitPriceProRata
    proRata
    converted
    totalDividends
    payOut {
      id
    }
    payBack
  }
`
const shareReturn = gql`
  fragment ShareReturn on ShareReturn {
    id
    investors {
      id
      name
    }
    share {
      id
      name
      seniority
      commonShare
      commonShareOptions
    }
    amount
    totalNumberOfShares
    exitPrice
    exitPriceLP
    exitPriceProRata
    proRata
    converted
    totalDividends
    payOut {
      id
    }
    payBack
  }
`

const investorReturnBase = gql`
  fragment InvestorReturnBase on InvestorReturn {
    investor {
      id
    }
    amount
    amountPreferred
    amountProRata
    totalNumberOfShares
    totalInvestments
    multiple
  }
`
const investorReturn = gql`
  fragment InvestorReturn on InvestorReturn {
    ...InvestorReturnBase
    investor {
      id
      name
      type
    }
    investorReturnEntries {
      ...ReturnEntry
    }
  }
  ${returnEntry}
  ${investorReturnBase}
`
const investorReturnIRRBase = gql`
  fragment InvestorReturnIRRBase on InvestorReturn {
    ...InvestorReturnBase
    amountSecondaries
    amountCNPayBack
    amountDebtPayBack
  }
  ${investorReturnBase}
`
const investorReturnIRR = gql`
  fragment InvestorReturnIRR on InvestorReturn {
    ...InvestorReturn
    amountSecondaries
    amountCNPayBack
    amountDebtPayBack
  }
  ${investorReturn}
`

const exitReturnBaseNoShares = gql`
  fragment ExitReturnBaseNoShares on ExitReturn {
    type
    exitAmount
    exitDate
    investorReturns {
      ...InvestorReturnIRRBase
    }
  }
  ${investorReturnIRRBase}
`
const exitReturnBaseNoInvestors = gql`
  fragment ExitReturnBaseNoInvestors on ExitReturn {
    type
    exitAmount
    exitDate
    shareReturns {
      ...ShareReturnBase
    }
  }
  ${shareReturnBase}
`
const exitReturnBase = gql`
  fragment ExitReturnBase on ExitReturn {
    type
    exitAmount
    exitDate
    investorReturns {
      ...InvestorReturnIRRBase
    }
    shareReturns {
      ...ShareReturnBase
    }
  }
  ${investorReturnIRRBase}
  ${shareReturnBase}
`
const exitReturn = gql`
  fragment ExitReturn on ExitReturn {
    type
    exitAmount
    exitDate
    investorReturns {
      ...InvestorReturnIRR
    }
    shareReturns {
      ...ShareReturn
    }
  }
  ${investorReturnIRR}
  ${shareReturn}
`

const exitReturnWithSteps = gql`
  fragment ExitReturnWithSteps on ExitReturn {
    ...ExitReturn
    returnSteps {
      ...ReturnStep
    }
  }
  ${exitReturn}
  ${returnStep}
`

const capTableBase = gql`
  fragment CapTableBase on CapTable {
    id
    calculationDate

    defaultRoundPrice
    preMoneyBasedPricePerShareFD
    preMoneyBasedPricePerShareND
    preMoneyBasedPricePerShareWithRoundCNsFD
    preMoneyBasedPricePerShareWithRoundCNsND
    preMoneyAsIfConvertedBasedPricePerShareFD
    averagePricePerShare

    totalShareInvestments

    postMoneyValuation
    preMoneyValuationAsIfConverted
    preMoneyValuationAsIfConvertedRoundBasis

    totalNumberOfShares
    totalNumberOfSharesFD
    totalNumberOfNewShares
    totalNumberOfNewSharesFD
    totalNumberOfPreferredShares
    ESOPAdjustPriceShares

    termsheet {
      round {
        ...RoundBase
      }
    }
  }
  ${roundBase}
`
const capTableLight = gql`
  fragment CapTableLight on CapTable {
    ...CapTableBase
    participations {
      ...ParticipationBase
    }
    untriggeredEvents {
      id
    }
  }
  ${participationBase}
  ${capTableBase}
`
const capTable = gql`
  fragment CapTable on CapTable {
    ...CapTableBase
    termsheet {
      round {
        id
        date
        ... on InvestmentRound {
          name
          preMoneyValuation
          postMoneyValuation
          cashIn
          type
          createdDate
          share {
            ...Share
          }
        }
      }
    }
    participations {
      ...ParticipationTrace
    }
    untriggeredEvents {
      ...Event
    }
  }
  ${participationTrace}
  ${capTableBase}
  ${event}
  ${share}
`
const capTableTrace = gql`
  fragment CapTableTrace on CapTable {
    ...CapTable
    importantExits {
      ...ExitReturn
    }
    sharePoints {
      ...SharePoint
    }
  }
  ${capTable}
  ${exitReturn}
  ${sharePoint}
`

const exitReturnSingleInvestor = gql`
  fragment ExitReturnSingleInvestor on ExitReturnSingleInvestor {
    type
    exitAmount
    exitDate
    investorAndEsopReturns {
      ...InvestorReturnIRR
    }
  }
  ${investorReturnIRR}
`

// SIMULATION (ranges, superNode, ...)

const doubleRange = gql`
  fragment DoubleRange on DoubleRange {
    start
    end
    stepSize
  }
`

const intRange = gql`
  fragment IntRange on IntRange {
    start
    end
    stepSize
  }
`

const interestRateRange = gql`
  fragment InterestRateRange on InterestRateRange {
    interestRate {
      ...DoubleRange
    }
    cumulative
    accumulationType
    limitInMonths {
      ...IntRange
    }
  }
  ${doubleRange}
  ${intRange}
`

const cnRange = gql`
  fragment CNRange on CNRange {
    dateOfDeposit
    ir {
      ...InterestRateRange
    }
    maturityDate
    discount {
      ...DoubleRange
    }
    preMoneyCap {
      ...DoubleRange
    }
  }
  ${interestRateRange}
  ${doubleRange}
`

const equityRange = gql`
  fragment EquityRange on EquityRange {
    preMoneyValuation {
      ...DoubleRange
    }
    lp {
      multiple {
        ...DoubleRange
      }
      simple
      agreedConversion
      capType
      cap {
        ...DoubleRange
      }
      ir {
        ...InterestRateRange
      }
    }
    ad {
      type
      compensation
      duration
      transferType
    }
  }
  ${doubleRange}
  ${interestRateRange}
`

const investorAmount = gql`
  fragment InvestorAmount on InvestorAmount {
    investorId
    investorName
    equityPercent
    cnPercent
  }
`

const superNode = gql`
  fragment SuperNode on SuperNode {
    # Only Output fields
    id
    # parent: SuperNode # Root if parent is null
    # children: [SuperNode!]! # Leaf if no children (empty array)
    size
    configSize
    # Also input fields
    name
    totalInvestmentAmount {
      ...DoubleRange
    }
    roundDate
    # Otional fields
    investmentsByInvestor {
      ...InvestorAmount
    } # IMPORTANT 0-100% of totalInvestments, rest will be given to "new investor"
    esopPoolSize {
      ...DoubleRange
    }
    equity {
      ...EquityRange
    }
    cn {
      ...CNRange
    }
  }
  ${doubleRange}
  ${cnRange}
  ${equityRange}
  ${investorAmount}
`

const calculationNode = gql`
  fragment CalculationNode on CalculationNode {
    id
    exitDate
    simulateAD
    specialClauses {
      ...PayOut
    }
    result: capTable {
      ...CapTableLight
    }
  }
  ${capTableLight}
  ${payOut}
`

// Node with limited recursion support (depth 8)
const modellingNode = gql`
  fragment ModellingNode on SuperNode {
    ...SuperNode
    children {
      ...SuperNode
      children {
        ...SuperNode
        children {
          ...SuperNode
          children {
            ...SuperNode
            children {
              ...SuperNode
              children {
                ...SuperNode
                children {
                  ...SuperNode
                }
              }
            }
          }
        }
      }
    }
  }
  ${superNode}
`

const calculationNodeRec = gql`
  fragment CalculationNodeRec on CalculationNode {
    ...CalculationNode
    children {
      ...CalculationNode
      children {
        ...CalculationNode
        children {
          ...CalculationNode
          children {
            ...CalculationNode
            children {
              ...CalculationNode
              children {
                ...CalculationNode
                children {
                  ...CalculationNode
                }
              }
            }
          }
        }
      }
    }
  }
  ${calculationNode}
`

// PROJECTS (Modelling and Base)

const modellingProject = gql`
  fragment ModellingProject on ModellingProject {
    id
    type
    name

    stakeholders {
      ...Investor
    }
    shares {
      ...Share
    }
    events {
      ...EventBase
    }

    simulationTree {
      roots {
        ...ModellingNode
      }
    }
    calculationTree {
      roots {
        ...CalculationNodeRec
      }
    }
    createdDate
  }
  ${investor}
  ${share}
  ${eventBase}
  ${modellingNode}
  ${calculationNodeRec}
`

const baseProjectRevision = gql`
  fragment ProjectRevision on BaseProject {
    id
    name
    roundingMode
    currencyCode
    simulateAD
    locked
    createdDate
  }
`

const baseProjectLight = gql`
  fragment ProjectLight on BaseProject {
    id
    name
    roundingMode
    currencyCode
    simulateAD
    locked
    createdDate
    hasPremiumAccess
    company {
      id
      name
    }
    projectMemberships {
      ...ProjectMembership
    }
  }
  ${projectMembership}
`

const baseProjectLightWithExits = gql`
  fragment ProjectLightWithExits on BaseProject {
    id
    name
    roundingMode
    currencyCode
    simulateAD
    locked
    createdDate
    company {
      id
      name
    }
    projectMemberships {
      ...ProjectMembership
    }
    defaultExits {
      project {
        id
        name
      }
      ...ExitReturn
    }
    capTables {
      ...CapTable
    }
  }
  ${capTable}
  ${exitReturn}
  ${projectMembership}
`

const baseProject = gql`
  fragment BaseProject on BaseProject {
    id
    name
    roundingMode
    currencyCode
    simulateAD
    locked
    createdDate
    hasPremiumAccess
    company {
      ...Company
    }
    projectMemberships {
      ...ProjectMembershipBase
    }
    projectInvitations {
      ...ProjectInvitationBase
    }
    projectDocuments {
      ...ProjectDocumentBase
    }
    stakeholders {
      ...Investor
    }
    shares {
      ...Share
    }
    payOuts {
      ...PayOut
    }
    events {
      ...EventBase
    }
    proposedEsopGrants {
      ...EventBase
    }
    capTables {
      ...CapTableLight
    }
  }
  ${company}
  ${projectMembershipBase}
  ${projectInvitationBase}
  ${projectDocumentBase}
  ${investor}
  ${share}
  ${payOut}
  ${eventBase}
  ${capTableLight}
`

const project = gql`
  fragment Project on Project {
    ...BaseProject
    ...ModellingProject
  }
  ${baseProject}
  ${modellingProject}
`

const fragments = {
  project, // ModellingProject OR BaseProject
  // Modelling
  modellingProject,
  superNode,
  modellingNode,
  calculationNodeRec,

  // Exit
  exitReturn,
  exitReturnBaseNoShares,
  exitReturnBaseNoInvestors,
  exitReturnBase,
  exitReturnSingleInvestor,
  exitReturnWithSteps,
  sharePoint,
  shareReturn,
  investorReturn,
  investorReturnIRR,

  // Calculation
  participationTrace,
  entry,
  capTableTrace,
  capTable,

  // Entities
  esopSetup,
  employeeLinkSetup,
  employeeLink,
  round,
  revisionRound,
  event,
  investor,
  investment,
  buyOut,
  share,
  shareInRound,
  baseProject,
  baseProjectRevision,
  baseProjectLight,
  baseProjectLightWithExits,
  esopGrant,
  esop,
  payOut,
  vestingSchedule,
  company,
  manualShares,
  manualSharesRevision,

  // User Utils
  userAccount,
  userGroup,
  userGroupInvitation,
  projectInvitation,
  projectMembership,
  projectDocument,

  // Auditing
  customRevisionEntity,

  // Other
  page,
}

export default fragments
